import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { OGLAttribution } from '../components/OGLAttribution';
import { 
  MagnifyingGlass, 
  Truck, 
  Lightning, 
  CheckCircle,
  ArrowRight,
  Database,
  ShieldCheck,
  Scales
} from '@phosphor-icons/react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-hub-623/artifacts/04kw0jh0_IMG_4683.jpeg';

export const LandingPage = () => {
  const features = [
    {
      icon: MagnifyingGlass,
      title: 'Smart Search',
      description: 'Find offences instantly by keyword, category, or severity level.'
    },
    {
      icon: Truck,
      title: 'Vehicle Coverage',
      description: 'Comprehensive reference for HGV, PSV, and LGV operators.'
    },
    {
      icon: Database,
      title: 'Official DVSA Data',
      description: 'All DVSA enforcement sanctions from the official May 2026 policy.'
    },
    {
      icon: Lightning,
      title: 'Fast Results',
      description: 'Mobile-first design for quick lookups in the field.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1551471698-c7787ff6b7ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHx0cnVjayUyMGxvZ2lzdGljcyUyMHVrfGVufDB8fHx8MTc3NjM5NTMxMXww&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-black/60" />
        
        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src={LOGO_URL} 
            alt="Vantage Fleet & Compliance" 
            className="h-24 sm:h-32 w-auto mx-auto mb-6 rounded-sm"
            data-testid="hero-logo"
          />
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4">
            Roadworthiness Guide
          </h1>
          
          <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
            The complete searchable database of DVSA enforcement sanctions. 
            Built for transport managers, fleet operators, and compliance professionals.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="min-w-[200px] h-12 text-base rounded-sm" data-testid="hero-cta-btn">
                Subscribe for £0.99/month
                <ArrowRight weight="bold" className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="lg" className="min-w-[200px] h-12 text-base rounded-sm border-white/30 text-white hover:bg-white/10" data-testid="hero-login-btn">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
              Everything You Need
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Access the complete DVSA enforcement policy database with powerful search and filtering.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-background p-8 hover:bg-muted/50 transition-colors duration-150"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <feature.icon weight="duotone" className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-muted/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
              Complete Coverage
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Browse sanctions across all major DVSA enforcement categories
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { name: 'Defects & Dimensions', icon: Truck },
              { name: "Drivers' Hours & Tachographs", icon: Scales },
              { name: 'Operator Licensing', icon: ShieldCheck },
              { name: 'Overloading', icon: Scales },
              { name: 'Driver Licensing', icon: CheckCircle },
              { name: 'Insurance', icon: ShieldCheck },
              { name: 'Driver CPC', icon: CheckCircle },
              { name: 'Dangerous Goods', icon: ShieldCheck },
            ].map(({ name, icon: Icon }) => (
              <div 
                key={name}
                className="border border-border bg-background p-6 text-center hover:border-foreground transition-colors duration-150"
              >
                <Icon weight="duotone" className="h-8 w-8 mx-auto text-primary mb-3" />
                <span className="text-sm font-medium">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
            Start Your Subscription Today
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Get instant access to the complete DVSA sanctions database for just £0.99 per month.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              variant="secondary" 
              className="h-12 px-8 text-base rounded-sm"
              data-testid="cta-subscribe-btn"
            >
              Subscribe Now
              <ArrowRight weight="bold" className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <img src={LOGO_URL} alt="Vantage" className="h-8 w-auto" />
              <span className="font-semibold text-sm">Vantage Fleet & Compliance</span>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              This tool is for guidance only and does not constitute legal advice. Based on DVSA Enforcement Sanctions Policy (May 2026).
            </p>
            <OGLAttribution className="text-center max-w-xl" />
          </div>
        </div>
      </footer>
    </div>
  );
};
